interface WebSocketMessage {
  type: string;
  message_id?: string;
  data: any;
}

interface ChatMessage {
  message_id: string;
  query: string;
  conversation_id: string;
}

type MessageHandler = (event: WebSocketMessage) => void;

class DirectWebSocketService {
  private ws: WebSocket | null = null;
  private messageHandlers = new Map<string, MessageHandler>();
  private errorHandlers = new Map<string, (error?: any) => void>();
  private wsUrl: string;
  private lastActivity = Date.now();
  private idleTimeout = 5 * 60 * 1000; // 5 minutes

  constructor(wsUrl: string) {
    this.wsUrl = wsUrl;
    this.startIdleTimer();
  }

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const { getAuthHeaders } = await import("src/utils/authUtils");
        const headers = await getAuthHeaders();

        // Add both auth tokens as query parameters with same names as headers
        const authToken = headers.authorization?.replace("Bearer ", "") || "";
        const federateToken =
          headers["federate-authorization"]?.replace("Bearer ", "") || "";

        const params = new URLSearchParams();
        if (authToken) params.append("Authorization", authToken);
        if (federateToken)
          params.append("federate-authorization", federateToken);

        const wsUrl = params.toString()
          ? `${this.wsUrl}/ws/chat?${params.toString()}`
          : `${this.wsUrl}/ws/chat`;

        this.ws = new WebSocket(wsUrl);
      } catch (error) {
        this.ws = new WebSocket(`${this.wsUrl}/ws/chat`);
      }

      this.ws.onopen = () => {
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.lastActivity = Date.now();

          const messageId = message.message_id || message.data?.message_id;
          if (messageId && this.messageHandlers.has(messageId)) {
            this.messageHandlers.get(messageId)!(message);

            // Clean up handler on completion
            if (message.type === "complete" || message.type === "error") {
              this.messageHandlers.delete(messageId);
              this.errorHandlers.delete(messageId);
            }
          } else if (message.type === "error" && this.errorHandlers.size > 0) {
            // Handle error messages without specific messageId
            const errorHandler = Array.from(this.errorHandlers.values())[0];
            errorHandler(message.data || message);
          }
        } catch (error) {
          console.error(
            "[WebSocket] Error parsing message:",
            error,
            "Raw data:",
            event.data,
          );
        }
      };

      this.ws.onclose = (event) => {
        // Call error handler for any abnormal closure
        if (event.code !== 1000) {
          // 1000 = normal closure
          const messageId = Array.from(this.messageHandlers.keys())[0];
          if (messageId && this.errorHandlers.has(messageId)) {
            this.errorHandlers.get(messageId)!({
              code: event.code,
              message: event.reason,
            });
          }
        }
      };

      this.ws.onerror = (error) => {
        // Call error handler if available
        const messageId = Array.from(this.messageHandlers.keys())[0];
        if (messageId && this.errorHandlers.has(messageId)) {
          this.errorHandlers.get(messageId)!(error);
        }
        reject(error);
      };
    });
  }

  async sendChatMessage(
    query: string,
    conversationId: string,
    messageId: string,
    handler: MessageHandler,
    errorHandler?: (error?: any) => void,
  ): Promise<void> {
    this.messageHandlers.set(messageId, handler);
    if (errorHandler) {
      this.errorHandlers.set(messageId, errorHandler);
    }

    const message: ChatMessage = {
      message_id: messageId,
      query,
      conversation_id: conversationId,
    };

    // Ensure connection is active
    if (this.ws?.readyState !== WebSocket.OPEN) {
      await this.connect();
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      this.lastActivity = Date.now();
    } else {
      this.messageHandlers.delete(messageId);
      this.errorHandlers.delete(messageId);
      throw new Error("WebSocket connection failed");
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.messageHandlers.clear();
    this.errorHandlers.clear();
  }

  private startIdleTimer(): void {
    setInterval(() => {
      const idleTime = Date.now() - this.lastActivity;
      if (idleTime > this.idleTimeout && this.messageHandlers.size === 0) {
        this.disconnect();
      }
    }, 60000); // Check every minute
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export default DirectWebSocketService;
